import { Fragment, HTMLProps, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { createColumnHelper, flexRender, getCoreRowModel, getExpandedRowModel, getSortedRowModel, SortingState, useReactTable, ExpandedState } from "@tanstack/react-table";

import { CategoryWithParts, ILeadPartsTableGlobalFilters, IPart } from "../../../../models/Part";
import { ArrowTopRightOnSquareIcon, ChevronRightIcon, LockClosedIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import classNames from "../../../../utils/classNames";
import { ebaySearchString } from "../../../../utils/ebaySearchString";
import openInNewTab from "../../../../utils/openInNewTab";
import LeadPartsCategoriesTableSubRow from "./LeadPartsCategoriesTableSubRow";
import { DateTime } from "luxon";
import LoadingPulse from "../../../shared/LoadingPulse";
import formatDateTime from "../../../../utils/formatDateTime";
import PriceFormat from "../../../../utils/priceFormat";
import EbayLinkButton from "../../../shared/Inputs/EbayLinkButton";
import { IVehicle } from "../../../../models/Vehicle";
import TableCheckboxCol from "../../../shared/Tables/IndeterminateCheckbox";
import useSingleSetting from "../../../../data/useSingleSetting";
import PartDiagramModal, { DiagramMode } from "../../../GtMotiveWidgets/PartDiagramModal";
import { PartProBlackList } from "../../../../models/PartLookup";
import {
  getPriorityTier,
  mockPriorityBreakdown,
  totalScoreFromBreakdown,
} from "../../../../utils/partPriority";
import PartPriorityChip from "../../../jobs/PartPriorityChip";

const colHelper = createColumnHelper<CategoryWithParts>();

/** Visual grammar shared with the Unlisted table cells (price / stock / MPN). */
const cardShell =
  "rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/95 to-white p-2 shadow-sm ring-1 ring-slate-200/50";
const innerTile =
  "rounded-lg bg-gradient-to-b from-slate-100/80 to-white px-2 py-1 ring-1 ring-inset ring-slate-200/70";
const tileLabel =
  "text-[9px] font-semibold uppercase tracking-wide text-slate-600/80";
const tileValue =
  "text-base font-semibold tabular-nums leading-tight text-slate-900";

/** Tone presets for the Pricing card — each suggestion tile gets its own
 *  subtle gradient + ring so the origin of the price (Last Sold, Market,
 *  Part Pro, RS Core) is readable at a glance. Mirrors the emerald/sky/
 *  indigo sold-tones in `UnlistedStockSummaryCell`. */
const priceTileTones = {
  lastSold:
    "from-sky-50/80 to-white ring-sky-200/60 text-sky-800 hover:from-sky-100/90",
  market:
    "from-rose-50/80 to-white ring-rose-200/60 text-rose-800 hover:from-rose-100/90",
  partPro:
    "from-wilma-50/80 to-white ring-wilma-200/60 text-wilma-700 hover:from-wilma-100/90",
  rs:
    "from-red-50/80 to-white ring-red-200/60 text-red-800 hover:from-red-100/90",
} as const;

interface PriceTileProps {
  label: string;
  price: number | null | undefined;
  tone: keyof typeof priceTileTones;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  locked?: boolean;
  title?: string;
}

/** Small click-to-fill suggestion tile used inside the Pricing card. Muted
 *  when no price is available; a single click copies the suggested price
 *  into the editable Offer price input above. */
function PriceTile({
  label,
  price,
  tone,
  onClick,
  disabled,
  loading,
  locked,
  title,
}: PriceTileProps) {
  const hasPrice = typeof price === "number" && price > 0;
  const isDisabled = disabled || loading || (!hasPrice && !locked);

  return (
    <button
      type="button"
      disabled={isDisabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!isDisabled && onClick) onClick();
      }}
      title={
        title ??
        (locked
          ? "Unlock Part Pro to view"
          : hasPrice
          ? `Set offer price to ${PriceFormat(price!)}`
          : `No ${label} price available`)
      }
      className={classNames(
        "group flex min-w-0 flex-1 basis-0 flex-col items-start rounded-lg bg-gradient-to-b px-2 py-1 text-left ring-1 ring-inset transition-colors",
        hasPrice && !isDisabled
          ? priceTileTones[tone] + " cursor-pointer"
          : "from-slate-50/80 to-white ring-slate-200/60 text-slate-400 cursor-not-allowed"
      )}
    >
      <span className="w-full truncate text-[9px] font-semibold uppercase tracking-wide opacity-80">
        {label}
      </span>
      {loading ? (
        <span className="pt-0.5">
          <LoadingPulse width="w-10" />
        </span>
      ) : locked ? (
        <span className="flex items-center justify-center gap-0.5 text-wilma-700">
          <LockClosedIcon className="h-3 w-3" />
        </span>
      ) : (
        <span className="w-full truncate text-xs font-semibold tabular-nums leading-tight">
          {hasPrice ? PriceFormat(price!) : "—"}
        </span>
      )}
    </button>
  );
}

/** Small icon-only eBay research button used inside tile cells where the
 *  standard `EbayLinkButton` is too bulky. Disabled state when there's no
 *  query so it's clear nothing will happen on click. */
function EbayResearchButton({
  query,
  disabled,
  title = "Research on eBay",
}: {
  query: string;
  disabled?: boolean;
  title?: string;
}) {
  const isDisabled = disabled || !query.trim();
  return (
    <button
      type="button"
      disabled={isDisabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!isDisabled) openInNewTab(ebaySearchString(query));
      }}
      title={isDisabled ? "Enter an MPN to search eBay" : title}
      className={classNames(
        "relative inline-flex flex-none items-center justify-center rounded-md p-0.5 ring-1 ring-inset transition-colors",
        isDisabled
          ? "bg-slate-50 ring-slate-200 opacity-50 cursor-not-allowed"
          : "bg-slate-100 ring-slate-200 hover:bg-slate-200 cursor-pointer"
      )}
    >
      <img src="/ebayLogo.svg" className="h-4 w-4" alt="" />
      <ArrowTopRightOnSquareIcon className="ml-0.5 h-2.5 w-2.5 text-gray-600" />
    </button>
  );
}

interface MpnSearchCellProps {
  lookupPartNumber?: string | null;
  partProUnlocked: boolean;
  partProLoading: boolean;
  diagram?: {
    show: boolean;
    linked: boolean; // true when we already have a zone+ucid match
    onClick: (e: React.MouseEvent) => void;
  };
}

/** MPN tile with a free-form input so users can type any part number and
 *  fire an eBay research query against it. Pre-fills with the Part Pro
 *  `lookupPartNumber` when it's available but stays editable either way,
 *  so users can research alternative / superseded MPNs without mutation.
 *  An optional diagram icon button is rendered alongside the eBay button
 *  when the vehicle has a UMC, to open the part-diagram modal. */
function MpnSearchCell({
  lookupPartNumber,
  partProUnlocked,
  partProLoading,
  diagram,
}: MpnSearchCellProps) {
  const [value, setValue] = useState<string>(lookupPartNumber ?? "");
  const touchedRef = useRef(false);

  // If the lookup populates later (e.g. after Part Pro unlocks), adopt the
  // fetched value — but don't clobber anything the user has already typed.
  useEffect(() => {
    if (touchedRef.current) return;
    setValue(lookupPartNumber ?? "");
  }, [lookupPartNumber]);

  return (
    <div className={classNames(innerTile, "flex flex-col")}>
      <div className="flex items-center justify-between gap-x-1">
        <span className={tileLabel}>MPN</span>
        {!partProUnlocked && !partProLoading && (
          <span
            className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-slate-500"
            title="Part Pro lookup is locked — you can still type an MPN"
          >
            <LockClosedIcon className="h-3 w-3" />
          </span>
        )}
      </div>
      {partProLoading ? (
        <div className="pt-0.5">
          <LoadingPulse width="w-20" />
        </div>
      ) : (
        <div className="mt-0.5 flex items-center gap-x-1">
          <input
            type="text"
            value={value}
            placeholder="Enter MPN"
            onChange={(e) => {
              touchedRef.current = true;
              setValue(e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
            className="w-full min-w-0 border-0 bg-transparent p-0 text-sm font-semibold tabular-nums leading-tight text-slate-900 placeholder:font-normal placeholder:text-slate-400 focus:outline-none focus:ring-0"
          />
          <EbayResearchButton query={value} />
          {diagram?.show && (
            <button
              type="button"
              onClick={diagram.onClick}
              title={diagram.linked ? "View part diagram" : "Find part diagram"}
              className={classNames(
                "inline-flex flex-none items-center justify-center rounded-md p-1 ring-1 ring-inset transition-colors cursor-pointer",
                diagram.linked
                  ? "bg-primary-50 text-primary-700 ring-primary-200 hover:bg-primary-100"
                  : "bg-slate-100 text-slate-600 ring-slate-200 hover:bg-slate-200"
              )}
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function IndeterminateCheckbox({
  indeterminate,
  className = "",
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (typeof indeterminate === "boolean") {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate]);

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + "p-3 cursor-pointer"}
      {...rest}
    />
  );
}


interface PricingCellProps {
  row: CategoryWithParts;
  offerPrice: number;
  partProUnlocked: boolean;
  partProLoading: boolean;
  hasRsKey: boolean;
  vehicle: IVehicle;
  handleSetTableData: (
    rowId: string,
    price: number | undefined,
    note?: string | undefined,
    isCore?: boolean | undefined
  ) => void;
}

/** Combined pricing card: editable "Offer price" on top with the four
 *  Wilma intelligence suggestion tiles (Last Sold, Market, Part Pro, RS
 *  Core) beneath. Clicking any tile copies that price into the offer
 *  input and selects the row (the upstream `handleSetTableData` already
 *  handles selection when a positive price is committed). */
function PricingCell({
  row,
  offerPrice,
  partProUnlocked,
  partProLoading,
  hasRsKey,
  vehicle,
  handleSetTableData,
}: PricingCellProps) {
  const [localPriceValue, setLocalPriceValue] = useState<number>(offerPrice);
  const inputRef = useRef<HTMLInputElement>(null);
  const commitTimerRef = useRef<number | undefined>(undefined);

  // Whenever the parent value changes (e.g. clicking a suggestion tile),
  // update localValue so the input shows the latest — unless the user is
  // actively editing, in which case we preserve their in-progress value.
  useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    setLocalPriceValue(offerPrice);
  }, [offerPrice]);

  useEffect(() => {
    return () => {
      if (typeof commitTimerRef.current !== "undefined") {
        window.clearTimeout(commitTimerRef.current);
      }
    };
  }, []);

  const rowId = row.category.id;
  const hasValue = typeof localPriceValue === "number" && localPriceValue > 0;

  // Last Sold — row aggregate takes priority, then category aggregate, then
  // fall back to the most recently sold part in the loaded parts array.
  const lastSoldPrice = useMemo(() => {
    if (row.lastSoldPrice != null) return row.lastSoldPrice;
    if (row.category.lastSoldPrice != null) return row.category.lastSoldPrice;
    return (
      row.parts
        ?.slice()
        .sort((a, b) => {
          if (!a.soldDate && !b.soldDate) return 0;
          if (!a.soldDate) return 1;
          if (!b.soldDate) return -1;
          const aDate = a.isInternal
            ? DateTime.fromISO(a.soldDate).toMillis()
            : DateTime.fromISO(a.created).toMillis();
          const bDate = b.isInternal
            ? DateTime.fromISO(b.soldDate).toMillis()
            : DateTime.fromISO(b.created).toMillis();
          return bDate - aDate;
        })
        .find((part) => part.soldDate)?.price ?? null
    );
  }, [row.lastSoldPrice, row.category.lastSoldPrice, row.parts]);

  const marketPrice = row.category.marketDataAveragePrice ?? null;

  // Part Pro: mirrors the visibility rules from `PartProPriceButton` so a
  // locked row still shows a tile (to prompt unlock) but blacklisted
  // makes / missing UCIDs suppress the tile entirely.
  const partProBlacklisted = PartProBlackList.includes(
    vehicle?.combinedMake?.toLowerCase() ?? ""
  );
  const hasUcid = !!row.category.ucid?.trim();
  const partProPrice = row.category.lookupAveragePrice ?? null;
  const showPartProTile = !partProBlacklisted && hasUcid;

  const rsPrice = row.category.rsPrice ?? null;
  const showRsTile = hasRsKey && rsPrice != null;

  const commitOffer = (value: number) => {
    if (typeof commitTimerRef.current !== "undefined") {
      window.clearTimeout(commitTimerRef.current);
    }
    commitTimerRef.current = window.setTimeout(() => {
      handleSetTableData(rowId, value);
    }, 0);
  };

  return (
    <div
      className={classNames(cardShell, "w-full min-w-[19rem]")}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex w-full items-stretch gap-1">
        <div
          className={classNames(
            "flex min-w-0 flex-col rounded-lg border border-dashed px-2 py-1 [flex:1.5_1.5_0%]",
            hasValue
              ? "border-emerald-300 bg-white"
              : "border-slate-300 bg-white hover:border-slate-400"
          )}
        >
          <div className={tileLabel}>Price</div>
          <div className="mt-0.5 flex items-center gap-0.5">
            <span
              className={classNames(
                "text-xs font-semibold tabular-nums",
                hasValue ? "text-emerald-700" : "text-slate-400"
              )}
            >
              £
            </span>
            <input
              ref={inputRef}
              type="number"
              value={localPriceValue}
              min={0}
              step="0.01"
              placeholder="0.00"
              onChange={(e) => {
                const raw = e.target.value;
                const parsed = raw === "" ? 0 : parseFloat(raw);
                setLocalPriceValue(Number.isNaN(parsed) ? 0 : parsed);
              }}
              onBlur={() => {
                if (localPriceValue !== offerPrice) {
                  commitOffer(localPriceValue);
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className={classNames(
                "w-full min-w-0 border-0 bg-transparent p-0 text-xs font-semibold tabular-nums leading-tight placeholder:font-normal placeholder:text-slate-400 focus:outline-none focus:ring-0",
                hasValue ? "text-slate-900" : "text-slate-500"
              )}
            />
          </div>
        </div>

        <PriceTile
          label="Last Sold"
          price={lastSoldPrice}
          tone="lastSold"
          onClick={() => {
            if (lastSoldPrice && lastSoldPrice > 0) {
              setLocalPriceValue(lastSoldPrice);
              commitOffer(lastSoldPrice);
            }
          }}
        />
        <PriceTile
          label="Market"
          price={marketPrice}
          tone="market"
          onClick={() => {
            if (marketPrice && marketPrice > 0) {
              setLocalPriceValue(marketPrice);
              commitOffer(marketPrice);
            }
          }}
        />
        <PriceTile
          label="Part Pro"
          price={partProPrice}
          tone="partPro"
          loading={partProLoading}
          locked={showPartProTile && !partProUnlocked && !partProLoading}
          disabled={!showPartProTile}
          title={
            !showPartProTile
              ? partProBlacklisted
                ? "Part Pro lookup not available for this make"
                : "No UCID available for this category"
              : undefined
          }
          onClick={() => {
            if (
              showPartProTile &&
              partProUnlocked &&
              partProPrice &&
              partProPrice > 0
            ) {
              setLocalPriceValue(partProPrice);
              commitOffer(partProPrice);
            }
          }}
        />
        <PriceTile
          label="RS Core"
          price={rsPrice}
          tone="rs"
          disabled={!hasRsKey}
          title={
            !hasRsKey
              ? "Add an RS Automotive API key in settings to enable"
              : "RS Automotive core price — marks this part as refurbished"
          }
          onClick={() => {
            if (hasRsKey && rsPrice && rsPrice > 0) {
              setLocalPriceValue(rsPrice);
              if (typeof commitTimerRef.current !== "undefined") {
                window.clearTimeout(commitTimerRef.current);
              }
              commitTimerRef.current = window.setTimeout(() => {
                handleSetTableData(rowId, rsPrice, undefined, true);
              }, 0);
            }
          }}
        />
      </div>
    </div>
  );
}

function EditableNoteCell({ rowId, value, handleSetTableData }: { rowId: string; value: string; handleSetTableData: (rowId: string, price: number | undefined, note?: string | undefined) => void; }) {
  const [localNoteValue, setLocalNoteValue] = useState<string>(value);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const commitTimerRef = useRef<number | undefined>(undefined);

  // Keep local state in sync if parent value changes (e.g., external updates)
  useEffect(() => {
    // Avoid clobbering the in-progress value while this field has focus.
    if (document.activeElement === textAreaRef.current) return;
    setLocalNoteValue(value ?? "");
  }, [value]);

  useEffect(() => {
    return () => {
      if (typeof commitTimerRef.current !== "undefined") {
        window.clearTimeout(commitTimerRef.current);
      }
    };
  }, []);

  return (
    <textarea
      ref={textAreaRef}
      value={localNoteValue}
      rows={1}
      onChange={(e) => {
        setLocalNoteValue(e.target.value);
      }}
      onBlur={() => {
        // Defer commit so focus transition to the next field can complete first.
        if (localNoteValue !== value) {
          const valueToCommit = localNoteValue;
          if (typeof commitTimerRef.current !== "undefined") {
            window.clearTimeout(commitTimerRef.current);
          }
          commitTimerRef.current = window.setTimeout(() => {
            handleSetTableData(rowId, undefined, valueToCommit);
          }, 0);
        }
      }}
      className="border p-1 text-sm w-full"
    />
  );
}

export interface LeadPartsCategoriesTableProps {
  categories: CategoryWithParts[];
  selectedCategories?: CategoryWithParts[];
  setSelectedCategories: React.Dispatch<React.SetStateAction<CategoryWithParts[]>>;
  partProUnlocked: boolean;
  partProLoading: boolean;
  vehicle: IVehicle;
  filter?: (cat: CategoryWithParts) => boolean;
  leadPartsTableFilters: ILeadPartsTableGlobalFilters;
}

export default function LeadPartsCategoriesTable({
  categories,
  selectedCategories,
  setSelectedCategories,
  partProUnlocked,
  partProLoading,
  vehicle,
  filter,
  leadPartsTableFilters,
}: LeadPartsCategoriesTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [tableData, setTableData] = useState<CategoryWithParts[]>(categories);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const { singleSetting } = useSingleSetting("Rs:ApiKey");
  const [hasRsKey, setHasRsKey] = useState(false);
  const [diagramOpen, setDiagramOpen] = useState(false);
  const [diagramMode, setDiagramMode] = useState<DiagramMode>({ type: "findAll" });
  const [diagramCategory, setDiagramCategory] = useState<CategoryWithParts["category"] | null>(null);
  const setSelectedCategoriesRef = useRef(setSelectedCategories);
  const tableDataRef = useRef(tableData);

  useEffect(() => {
    setSelectedCategoriesRef.current = setSelectedCategories;
  }, [setSelectedCategories]);

  useEffect(() => {
    tableDataRef.current = tableData;
  }, [tableData]);

  const filteredTableData = useMemo(() => {
    return filter ? tableData.filter(filter) : tableData;
  }, [tableData, filter]);

  const rowSelection = useMemo(() => {
    const sel: Record<string, boolean> = {};
    selectedCategories?.forEach((cat) => {
      sel[cat.category.id] = true;
    });
    return sel;
  }, [selectedCategories]);

  const handleSetTableData = useCallback((
    rowId: string,
    newPrice: number | undefined,
    newNote: string | undefined = undefined,
    isCore: boolean | undefined = undefined // Is refurbished / Rs
  ) => {
    const currentTableData = tableDataRef.current;
    const existingRow = currentTableData.find((item) => item.category.id === rowId);
    if (!existingRow) return;

    const hasNumericPrice = typeof newPrice === "number" && !Number.isNaN(newPrice);
    const numericPrice = hasNumericPrice ? newPrice : undefined;
    const shouldUpdateNote = typeof newNote !== "undefined";
    const shouldUpdateCore = typeof isCore === "boolean";

    const nextPrice = hasNumericPrice ? numericPrice : existingRow.price;
    const nextNote = shouldUpdateNote ? newNote : existingRow.note;
    const nextIsCore = shouldUpdateCore ? isCore : existingRow.isCore;

    const updatedRow: CategoryWithParts = {
      ...existingRow,
      price: nextPrice,
      note: nextNote,
      isCore: nextIsCore
    };

    const nextSelectedUpdater = (prevSelectedForThisTable: CategoryWithParts[]) => {
      const selectedIndex = prevSelectedForThisTable.findIndex(
        (cat) => cat.category.id === rowId
      );
      const isCurrentlySelected = selectedIndex !== -1;

      if (hasNumericPrice) {
        if (numericPrice! <= 0) {
          if (!isCurrentlySelected) return prevSelectedForThisTable;
          return prevSelectedForThisTable.filter((cat) => cat.category.id !== rowId);
        }

        if (!isCurrentlySelected) {
          return [...prevSelectedForThisTable, updatedRow];
        }

        const nextSelected = [...prevSelectedForThisTable];
        nextSelected[selectedIndex] = updatedRow;
        return nextSelected;
      }

      if (shouldUpdateNote && isCurrentlySelected) {
        const nextSelected = [...prevSelectedForThisTable];
        nextSelected[selectedIndex] = updatedRow;
        return nextSelected;
      }

      return prevSelectedForThisTable;
    };

    setSelectedCategoriesRef.current(nextSelectedUpdater);

    const didChange =
      nextPrice !== existingRow.price ||
      nextNote !== existingRow.note ||
      nextIsCore !== existingRow.isCore;

    if (!didChange) return;

    setTableData((old) => old.map((item) => (
      item.category.id === rowId ? updatedRow : item
    )));
  }, []);

  const makeModelString = useMemo(() => {
    let string = "";

    if (vehicle?.dvlaYearOfManufacture) {
      string = vehicle?.dvlaYearOfManufacture + "+";
    }

    string += vehicle?.combinedMake;
    string += "+";
    string += vehicle?.modelRangeDescription;

    return string;
  }, [vehicle])

  const baseCols = useMemo(() => [
    {
      id: 'dropdown',
      cell: ({ row }: any) => {
        const categoryId = row.original.category.id;
        const isExpanded = row.getIsExpanded();

        return (
          <div className="flex justify-center items-center flex-1 p-4" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                row.toggleExpanded();
              }}
              style={{ transform: `rotate(${!isExpanded ? "0" : "-270"}deg)` }}
              className="p-1 transition duration-500 cursor-pointer border border-gray-400 rounded-full hover:bg-gray-200"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
    colHelper.accessor("category.name", {
      id: "category.name",
      header: "Part",
      cell: (info) => {
        const name = info.getValue();
        const cat = info.row.original.category;
        // Mock priority breakdown for now — keyed off the category id
        // (with vehicle id as `seedExtra`) so the same vehicle always
        // recommends the same parts. Swap for a real `priorityBreakdown`
        // field on the category as soon as the backend exposes one.
        const priorityBreakdown = mockPriorityBreakdown(
          cat.id,
          vehicle?.id ?? undefined
        );
        const priorityScore = totalScoreFromBreakdown(priorityBreakdown);
        const priorityTier = getPriorityTier(priorityScore);
        return (
          <div
            className={classNames(cardShell, "min-w-[18rem] max-w-[28rem]")}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={classNames(innerTile, "flex flex-col")}>
              <div className={tileLabel}>Part</div>
              <div className="mt-0.5 flex items-start justify-between gap-x-2">
                <span
                  className="flex-1 text-sm font-semibold leading-snug text-slate-900"
                  title={name}
                >
                  {name}
                </span>
                <EbayLinkButton query={makeModelString + "+" + name} />
              </div>
              <div className="mt-1.5 flex items-center gap-1.5">
                <PartPriorityChip
                  score={priorityScore}
                  tier={priorityTier}
                  breakdown={priorityBreakdown}
                  trailing={
                    <div
                      className="h-1 flex-1 overflow-hidden rounded-full bg-slate-100"
                      aria-hidden="true"
                    >
                      <div
                        className={classNames(
                          "h-full rounded-full",
                          priorityTier.barClass
                        )}
                        style={{ width: `${priorityScore}%` }}
                      />
                    </div>
                  }
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        );
      },
    }),
    colHelper.display({
      id: "lookupMpn",
      header: "MPN",
      cell: (info) => {
        const cat = info.row.original.category;

        const hasZone = !!cat.ucidZone;
        const hasUcid = !!cat.ucid;
        const showDiagram = !!vehicle?.umc;

        const handleDiagramClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          setDiagramCategory(cat);
          if (hasZone && hasUcid) {
            setDiagramMode({ type: "view", zoneId: cat.ucidZone, ucid: cat.ucid });
          } else if (hasZone) {
            setDiagramMode({ type: "findZone", zoneId: cat.ucidZone });
          } else {
            setDiagramMode({ type: "findAll" });
          }
          setDiagramOpen(true);
        };

        return (
          <div
            className={classNames(cardShell, "w-[17rem] min-w-[16rem] max-w-[18rem]")}
            onClick={(e) => e.stopPropagation()}
          >
            <MpnSearchCell
              key={cat.id}
              lookupPartNumber={cat.lookupPartNumber}
              partProUnlocked={partProUnlocked}
              partProLoading={partProLoading}
              diagram={
                showDiagram
                  ? {
                      show: true,
                      linked: hasZone && hasUcid,
                      onClick: handleDiagramClick,
                    }
                  : undefined
              }
            />
          </div>
        );
      }
    }),
    colHelper.accessor((row) => row.price, {
      id: "pricing",
      header: "Pricing",
      sortingFn: "auto",
      cell: (info) => {
        const row = info.row.original;
        const currentValue = info.getValue() as number;
        return (
          <PricingCell
            row={row}
            offerPrice={currentValue}
            partProUnlocked={partProUnlocked}
            partProLoading={partProLoading}
            hasRsKey={hasRsKey}
            vehicle={vehicle}
            handleSetTableData={handleSetTableData}
          />
        );
      },
    }),
    colHelper.accessor(
      row => {
        // Use aggregate field if available, otherwise compute from parts
        if (row.internalStock !== undefined) {
          return row.internalStock;
        }
        return row.parts?.filter(part => !part.soldDate).length ?? 0;
      },
      {
        id: "partsCount",
        header: "Stock",
        cell: (info) => {
          const row = info.row.original;
          const partsCount = info.getValue() as number;
          const partProCount = 0; //don't have this data yet
          const totalSold =
            row.totalSold !== undefined
              ? row.totalSold
              : row.parts?.filter((x) => x.soldDate)?.length ?? 0;
          const lastSold =
            row.lastSoldDate ??
            row.parts
              ?.filter((x) => x.soldDate)
              ?.sort((a, b) => (a.soldDate! < b.soldDate! ? 1 : -1))[0]
              ?.soldDate;
          const hasInternal = partsCount > 0;
          const hasSold = totalSold > 0;

          return (
            <div
              className={classNames(cardShell, "min-w-[11rem]")}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="grid grid-cols-3 gap-1">
                <div
                  className={classNames(
                    "rounded-lg bg-gradient-to-b px-2 py-1 ring-1 ring-inset",
                    hasInternal
                      ? "from-emerald-50/80 to-white text-emerald-900 ring-emerald-200/70"
                      : "from-slate-100/80 to-white ring-slate-200/70"
                  )}
                  title={`${partsCount} in internal stock`}
                >
                  <div className={tileLabel}>Internal</div>
                  <div
                    className={classNames(
                      "text-sm font-semibold tabular-nums leading-tight",
                      hasInternal ? "text-emerald-800" : "text-slate-400"
                    )}
                  >
                    {hasInternal ? partsCount : "—"}
                  </div>
                </div>

                <div
                  className="rounded-lg bg-gradient-to-b from-wilma-50/80 to-white px-2 py-1 ring-1 ring-inset ring-wilma-200/60"
                  title="Part Pro lookup stock"
                >
                  <div className={tileLabel}>Part Pro</div>
                  {partProLoading ? (
                    <LoadingPulse width="w-10" />
                  ) : partProUnlocked ? (
                    <div
                      className={classNames(
                        "text-sm font-semibold tabular-nums leading-tight",
                        partProCount > 0 ? "text-wilma-700" : "text-slate-400"
                      )}
                    >
                      {partProCount > 0 ? partProCount : "—"}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-slate-500 leading-tight">
                      <LockClosedIcon className="h-3 w-3" />
                    </div>
                  )}
                </div>

                <div
                  className={classNames(
                    "rounded-lg bg-gradient-to-b px-2 py-1 ring-1 ring-inset",
                    hasSold
                      ? "from-sky-50/80 to-white text-sky-900 ring-sky-200/60"
                      : "from-slate-100/80 to-white ring-slate-200/70"
                  )}
                  title={
                    hasSold
                      ? `${totalSold} previously sold${
                          lastSold
                            ? ` · last sold ${formatDateTime(
                                lastSold,
                                DateTime.DATE_MED
                              )}`
                            : ""
                        }`
                      : "No previous sales recorded"
                  }
                >
                  <div className={tileLabel}>Sold</div>
                  <div
                    className={classNames(
                      "text-sm font-semibold tabular-nums leading-tight",
                      hasSold ? "text-sky-800" : "text-slate-400"
                    )}
                  >
                    {hasSold ? totalSold : "—"}
                  </div>
                </div>
              </div>
            </div>
          );
        }
      }),
    colHelper.accessor("note",
      {
        id: "note",
        header: "Internal Note",
        enableSorting: false,
        cell: (info) => {
          const currentValue = info.getValue();
          const rowId = info.row.original.category.id;
          return (
            <EditableNoteCell
              rowId={rowId}
              value={currentValue ?? ""}
              handleSetTableData={handleSetTableData}
            />
          );
        }
      }),
    TableCheckboxCol(false),
  ], [handleSetTableData, makeModelString, partProLoading, partProUnlocked, hasRsKey, vehicle]);

  useEffect(() => {
    if (singleSetting.data?.data && singleSetting.data.data.value != "" && singleSetting.data.data.value != null) {
      setHasRsKey(true);
    } else {
      setHasRsKey(false);
    }
  }, [singleSetting.data, singleSetting.isLoading]);

  // RS Core pricing is rendered inline inside the combined Pricing cell
  // (gated on `hasRsKey`), so the separate RS column has been retired.
  const cols = baseCols;


  const table = useReactTable({
    data: filteredTableData,
    columns: cols,
    getRowId: (row) => row.category.id,
    state: {
      sorting: sorting,
      rowSelection: rowSelection,
      expanded: expanded
    },
    enableRowSelection: true,
    onRowSelectionChange: (updaterOrState) => {
      // If it's a function, call it with the current state to get newState.
      const newState: Record<string, boolean> =
        typeof updaterOrState === "function"
          ? updaterOrState(rowSelection)
          : updaterOrState;

      const newlySelected: CategoryWithParts[] = [];
      for (const [rowId, isSel] of Object.entries(newState)) {
        if (isSel) {
          const found = tableData.find((c) => c.category.id === rowId);
          if (found) newlySelected.push(found);
        }
      }
      setSelectedCategoriesRef.current(newlySelected);
    },
    onExpandedChange: (updaterOrValue) => {
      if (typeof updaterOrValue === 'function') {
        setExpanded(prev => {
          const newValue = updaterOrValue(prev);
          // Ensure only one row is expanded at a time
          if (typeof newValue === 'boolean') return {};
          const trueKeys = Object.keys(newValue).filter(key => newValue[key]);
          if (trueKeys.length > 1) {
            // Keep only the last one (most recently expanded)
            const lastKey = trueKeys[trueKeys.length - 1];
            return { [lastKey]: true };
          }
          return newValue;
        });
      } else {
        if (typeof updaterOrValue === 'boolean') {
          setExpanded({});
        } else {
          // Ensure only one row is expanded
          const trueKeys = Object.keys(updaterOrValue).filter(key => updaterOrValue[key]);
          if (trueKeys.length > 1) {
            const lastKey = trueKeys[trueKeys.length - 1];
            setExpanded({ [lastKey]: true });
          } else {
            setExpanded(updaterOrValue);
          }
        }
      }
    },
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    setTableData(prev => {
      const prevById = new Map(prev.map(c => [c.category.id, c]));

      return categories.map(incoming => {
        const local = prevById.get(incoming.category.id);
        if (!local) return incoming;

        return {
          ...incoming,
          // Preserve user-entered local fields
          ...(local.price != null ? { price: local.price } : {}),
          ...(local.note != null ? { note: local.note } : {}),
          ...(local.isCore != null ? { isCore: local.isCore } : {}),
          category: {
            ...incoming.category,
          },
        };
      });
    });
  }, [categories]);

  return (
    <>
      <div className="overflow-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">

          {/* Table headings - using nth child to make the name column the same width */}
          {/* Also using first and last to make sure the dropdown and select boxes are the same width */}
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-primary-50 text-gray-800 [&>*:nth-child(2)]:w-36" >
                {headerGroup.headers.map((header, i) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="flex first:w-16 last:w-16 py-0.5 px-2 text-xs font-semibold uppercase tracking-wide lg:table-cell text-left"
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>

                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {/* Body */}
          <tbody>
            {table.getRowModel().rows.map((row, rowIndex) => {
              let rowCells = row.getVisibleCells();
              let isSelected = row.getIsSelected();
              let noPriceSelected = isSelected && (!row.original.price || row.original.price <= 0);

              return (
                <Fragment key={row.id}>
                  <tr
                    key={row.id}
                    // onClick={() => setLocation(row.original)}
                    className={classNames(
                      "transition-colors",
                      noPriceSelected
                        ? "bg-red-200"
                        : isSelected
                          ? "bg-emerald-50 ring-1 ring-inset ring-emerald-200"
                          : rowIndex % 2 === 1
                            ? "bg-slate-50/60"
                            : "bg-white",
                      "hover:bg-gray-100"
                    )}
                  >
                    {rowCells.map((cell) => (
                      <td key={cell.id} className="px-3 border text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                    <tr>
                      {/* 2nd row is a custom 1 cell row */}
                      <td colSpan={row.getVisibleCells().length}>
                        <LeadPartsCategoriesTableSubRow
                          categoryWithParts={row.original}
                          vehicleId={vehicle.id}
                          vehicleYear={vehicle.dvlaYearOfManufacture ?? null}
                          vehicleVisibilityDate={vehicle.visibilityDate ?? null}
                          vehicleTerminateDate={vehicle.terminateDate ?? null}
                          vehicleModelSeries={vehicle.modelSeries ?? null}
                          leadPartsTableFilters={leadPartsTableFilters}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>

        </table>
      </div>

      {vehicle?.umc && diagramCategory && (
        <PartDiagramModal
          open={diagramOpen}
          setOpen={setDiagramOpen}
          umc={vehicle.umc}
          category={diagramCategory}
          mode={diagramMode}
        />
      )}
    </>
  );
}
